import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/jobsiq/Navbar";
import Footer from "../components/jobsiq/Footer";
import { api } from "../lib/api";
import MatchBar from "../components/jobsiq/MatchBar";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, XCircle, UserCircle, VideoCamera, FileText } from "@phosphor-icons/react";
import VerifiedBadge from "../components/jobsiq/VerifiedBadge";

export default function Applicants() {
    const { jobId } = useParams();
    const [apps, setApps] = useState([]);
    const [job, setJob] = useState(null);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        const [j, a] = await Promise.all([
            api.get(`/jobs/${jobId}`),
            api.get(`/applications/job/${jobId}`),
        ]);
        setJob(j.data);
        setApps(a.data);
        if (a.data[0]) setSelected(a.data[0]);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId]);

    const setStatus = async (id, status) => {
        try {
            await api.put(`/applications/${id}/status`, null, { params: { status } });
            toast.success(`Marked ${status}`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        }
    };

    return (
        <div data-testid="applicants-page">
            <Navbar />
            <section className="max-w-[1400px] mx-auto px-6 py-8">
                <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-950">
                    <ArrowLeft size={14} /> Dashboard
                </Link>
                <div className="mt-4 flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-600">
                            Applicants · AI-ranked
                        </div>
                        <h1 className="font-display text-4xl mt-1">{job?.title}</h1>
                        <div className="text-slate-600 text-sm mt-1">
                            {apps.length} applicant{apps.length === 1 ? "" : "s"} · sorted by match score
                        </div>
                    </div>
                </div>

                <div className="mt-6 grid md:grid-cols-[360px_1fr] gap-6">
                    <div className="border border-slate-200 divide-y divide-slate-200 max-h-[70vh] overflow-auto">
                        {apps.length === 0 && (
                            <div className="p-6 text-sm text-slate-500">No applicants yet.</div>
                        )}
                        {apps.map((a) => (
                            <button
                                key={a.application_id}
                                data-testid={`applicant-row-${a.application_id}`}
                                onClick={() => setSelected(a)}
                                className={`w-full text-left p-4 hover:bg-slate-50 ${
                                    selected?.application_id === a.application_id ? "bg-slate-50 border-l-4 border-blue-600" : ""
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <UserCircle size={28} weight="duotone" className="text-slate-400" />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="font-display text-lg truncate">{a.seeker_name}</span>
                                            {a.seeker_verified && <VerifiedBadge testId={`applicant-verified-${a.application_id}`} />}
                                        </div>
                                        <div className="text-xs text-slate-500 truncate">
                                            {a.seeker_title || "—"} · {a.seeker_experience || 0} yrs
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-2">
                                    <MatchBar score={a.match_score} />
                                </div>
                                <div className="mt-1 text-[10px] font-mono-plex uppercase tracking-widest text-slate-500">
                                    {a.status}
                                </div>
                            </button>
                        ))}
                    </div>

                    <div>
                        {selected ? (
                            <div className="border border-slate-200 p-6" data-testid="applicant-detail">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div>
                                        <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500">
                                            {selected.seeker_email}
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap mt-1">
                                            <h2 className="font-display text-3xl">{selected.seeker_name}</h2>
                                            {selected.seeker_verified && <VerifiedBadge size="md" tone="light" testId="applicant-detail-verified" />}
                                        </div>
                                        <div className="text-slate-600 text-sm">
                                            {selected.seeker_title || "—"} · {selected.seeker_experience || 0} years
                                        </div>
                                    </div>
                                    <MatchBar score={selected.match_score} size="lg" />
                                </div>

                                <div className="mt-6 grid md:grid-cols-2 gap-6">
                                    <div>
                                        <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-2">
                                            Skill overlap
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {(selected.match_detail?.skill_overlap || []).map((s) => (
                                                <span key={s} className="text-xs px-2 py-1 bg-blue-600 text-white font-mono-plex">
                                                    {s}
                                                </span>
                                            ))}
                                            {(selected.match_detail?.missing_skills || []).map((s) => (
                                                <span key={s} className="text-xs px-2 py-1 border border-slate-300 text-slate-500 font-mono-plex line-through">
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-2">
                                            AI verdict
                                        </div>
                                        <p className="text-sm text-slate-700">{selected.match_detail?.verdict || "—"}</p>
                                    </div>
                                </div>

                                <div className="mt-6 grid md:grid-cols-2 gap-6 text-sm">
                                    <div className="border border-slate-200 p-4">
                                        <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-1">Strengths</div>
                                        <p>{selected.match_detail?.strengths || "—"}</p>
                                    </div>
                                    <div className="border border-slate-200 p-4">
                                        <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-1">Concerns</div>
                                        <p>{selected.match_detail?.concerns || "—"}</p>
                                    </div>
                                </div>

                                {selected.video_analysis && (
                                    <div className="mt-6 border border-slate-200 p-5">
                                        <div className="font-mono-plex text-xs uppercase tracking-widest text-blue-600 inline-flex items-center gap-1">
                                            <VideoCamera size={14} /> Video interview intelligence
                                        </div>
                                        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                                            {[
                                                ["Overall", selected.video_analysis.overall_score],
                                                ["Communication", selected.video_analysis.communication],
                                                ["Clarity", selected.video_analysis.clarity],
                                                ["Confidence", selected.video_analysis.confidence],
                                                ["Engagement", selected.video_analysis.engagement],
                                                ["Warmth", selected.video_analysis.warmth],
                                                ["Composure", selected.video_analysis.composure],
                                            ].filter(([, v]) => v !== undefined && v !== null).map(([k, v]) => (
                                                <div key={k} className="border border-slate-200 p-2 text-center">
                                                    <div className="font-display text-xl">{v ?? "—"}</div>
                                                    <div className="text-[10px] font-mono-plex uppercase tracking-widest text-slate-500">{k}</div>
                                                </div>
                                            ))}
                                        </div>
                                        {selected.video_analysis.emotional_cues?.length > 0 && (
                                            <div className="mt-3 flex flex-wrap gap-1.5">
                                                {selected.video_analysis.emotional_cues.map((c) => (
                                                    <span key={c} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 font-mono-plex">
                                                        {c}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <p className="mt-3 text-sm text-slate-700">{selected.video_analysis.summary}</p>
                                        {selected.video_transcript && (
                                            <details className="mt-3 text-xs text-slate-600">
                                                <summary className="cursor-pointer font-mono-plex uppercase tracking-widest">Transcript</summary>
                                                <p className="mt-2 whitespace-pre-wrap">{selected.video_transcript}</p>
                                            </details>
                                        )}
                                    </div>
                                )}

                                {selected.resume_path && (
                                    <div className="mt-6 flex items-center gap-2 text-sm">
                                        <FileText size={16} /> Résumé attached (available via Jobs IQ storage)
                                    </div>
                                )}

                                <div className="mt-8 flex flex-wrap gap-2">
                                    <button
                                        data-testid="shortlist-btn"
                                        onClick={() => setStatus(selected.application_id, "shortlisted")}
                                        className="px-4 py-2 bg-blue-600 text-white inline-flex items-center gap-1.5 hover:bg-slate-950"
                                    >
                                        <CheckCircle size={16} /> Shortlist
                                    </button>
                                    <button
                                        data-testid="reviewing-btn"
                                        onClick={() => setStatus(selected.application_id, "reviewing")}
                                        className="px-4 py-2 border border-slate-300 hover:bg-slate-950 hover:text-white"
                                    >
                                        Mark reviewing
                                    </button>
                                    <button
                                        data-testid="hire-btn"
                                        onClick={() => setStatus(selected.application_id, "hired")}
                                        className="px-4 py-2 border border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                                    >
                                        Hire
                                    </button>
                                    <button
                                        data-testid="reject-btn"
                                        onClick={() => setStatus(selected.application_id, "rejected")}
                                        className="px-4 py-2 border border-slate-300 text-slate-500 hover:border-red-600 hover:text-red-600 inline-flex items-center gap-1.5"
                                    >
                                        <XCircle size={16} /> Reject
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="border border-dashed border-slate-300 p-12 text-center text-slate-500 text-sm">
                                Select an applicant to see details.
                            </div>
                        )}
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}
